import {
    Avatar,
    Badge,
    Button,
    Card,
    Flexer,
    Form,
    FormControl,
    FormHelperText,
    FormLabel,
    Heading,
    Icon,
    IconLib,
    Input,
    InputControl,
    InputPrefix,
    Label,
    Link,
    Modal,
    ModalClose,
    Option,
    Options,
    Portal,
    Range,
    Select,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    View,
    plural,
    useDialog,
    useInput,
    useToast,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbSparkles, TbTrash } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { ColorButton, Error, Loading, LoadingLine, Tiptap } from '../components'
import { getMentionsfromTipTapJSON, logger, urlContainsId } from '../helpers/util'
import { useWorkspaces } from '../hooks/api.hooks'
import { useFile } from '../hooks/file.hook'
import { Disclaimer } from '../layouts/auth.layout'
import { useAppStore } from '../store/app'
import { COLORS } from '../constants'

function generateWorkingHours() {
    const hours: string[] = []
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
            const hour = h.toString().padStart(2, '0')
            const minute = m.toString().padStart(2, '0')
            hours.push(`${hour}:${minute}`)
        }
    }
    return hours
}

const workingHours = generateWorkingHours()

export const WorkspaceModal = ({ onDismiss }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const [seats, setSeats] = useState(1)
    const [plan, setPlan] = useState<'user' | 'team' | ''>('')
    const [selected, setSelected] = useState(0)
    const [color, setColor] = useState('')
    const [description, setDescription] = useState('')
    const { setDialog, closeDialog } = useDialog()
    const workspacesAPI = useWorkspaces()
    const navigate = useNavigate()
    const { workspaceId, projectId } = useParams()
    const name = useInput('')
    const [workHoursEnd, setWorkHoursEnd] = useState('')
    const [workHoursStart, setWorkHoursStart] = useState('')
    const [weekLength, setWeekLength] = useState(0)
    const [image, setImage] = useState(null)
    const fileRef = useRef<any>(null)
    const { upload, getStorableUrl } = useFile()
    const { getOne, update } = useWorkspaces()
    const { showToast } = useToast()
    const actions = useAppStore((state) => state.actions)
    const [subscriptionId, setSubscriptionId] = useState('')
    const [subscriptionPlan, setSubscriptionPlan] = useState('')
    const [subscriptionQty, setSubscriptionQty] = useState(1)
    const [role, setRole] = useState('')
    const isAdmin = useMemo(() => role == 'Owner' || role == 'Admin', [role])
    const workHoursStartSelect = useMemo(() => (workHoursStart ? [workHoursStart] : []), [workHoursStart])
    const workHoursEndSelect = useMemo(() => (workHoursEnd ? [workHoursEnd] : []), [workHoursEnd])

    const handleFileChange = async (e) => {
        if (e.target.files.length == 0) return

        setLoading(true)
        setError(null)

        try {
            const { url } = await upload({
                folder: workspaceId,
                file: e.target.files[0],
                secure: false,
            })

            setImage(url)
            setLoading(false)

            actions.workspaceUpdate({ image: url })
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        try {
            const workspace = {
                name: name.value,
                description,
                image: image ? getStorableUrl(image) : null,
                color,
                workHoursStart,
                workHoursEnd,
                weekLength,
            }

            actions.workspaceUpdate({ ...workspace })

            showToast({
                message: 'Workspsace updated.',
                anchor: 'bottom-right',
                prefix: <IconLib icon="check" />,
            })

            await update(workspaceId, workspace)

            e.target.value = ''
        } catch (e) {
            logger(e)
            setError('Something has gone wrong.')
            e.target.value = ''
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const workspace = await getOne(workspaceId)

            setWorkHoursStart(workspace.workHoursStart)
            setWorkHoursEnd(workspace.workHoursEnd)
            setWeekLength(workspace.weekLength)

            setSubscriptionId(workspace.subscriptionId)
            setSubscriptionPlan(workspace.subscriptionPlan)
            setSubscriptionQty(workspace.subscriptionQty)

            setSeats(workspace.subscriptionQty || 1)
            setPlan(workspace.subscriptionPlan || '')

            name.setValue(workspace.name)
            setDescription(workspace.description)

            setRole(workspace.role)
            setImage(workspace.image)
            setColor(workspace.color || '')
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleSubscriptionChange = async (plan) => {
        setDialog({
            title: 'Are you sure?',
            description: 'Changing your subscription will impact pricing & users.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button
                        disabled={loading}
                        onClick={closeDialog}>
                        Cancel
                    </Button>
                    <Button
                        disabled={loading}
                        onClick={() => {
                            setLoading(true)
                            closeDialog()

                            workspacesAPI
                                .changeSubscription(workspaceId, subscriptionId, { seats, plan })
                                .then(() => {
                                    setSeats(seats)
                                    setPlan(plan)
                                    setSubscriptionQty(seats)
                                    setSubscriptionPlan(plan)
                                    setLoading(false)

                                    actions.workspaceUpdate({
                                        subscriptionQty: seats,
                                        subscriptionPlan: plan,
                                    })
                                })
                                .catch(() => {
                                    setLoading(false)
                                    setError('Something has gone wrong')
                                })
                        }}
                        variant="danger">
                        Change
                    </Button>
                </View>
            ),
        })
    }

    const handleDelete = async () => {
        setDialog({
            title: 'Are you sure?',
            description: 'You will lose access to this workspace and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button
                        disabled={loading}
                        onClick={closeDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setLoading(true)
                            closeDialog()

                            workspacesAPI
                                .remove(workspaceId)
                                .then(() => {
                                    onDismiss()
                                    urlContainsId(workspaceId, () => navigate(`/app`))
                                    setLoading(false)
                                })
                                .catch(() => {
                                    setLoading(false)
                                    setError('Something has gone wrong')
                                })
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleSubscriptionDelete = async () => {
        setDialog({
            title: 'Are you sure?',
            description:
                'Removing your subscription will affect user access. You will still have access for the remainder of your billing period.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button
                        disabled={loading}
                        onClick={closeDialog}>
                        Cancel
                    </Button>
                    <Button
                        disabled={loading}
                        onClick={() => {
                            setLoading(true)
                            closeDialog()

                            workspacesAPI
                                .cancelSubscription(workspaceId, subscriptionId)
                                .then(() => {
                                    setSubscriptionId(null)
                                    setSeats(0)
                                    setPlan('')
                                    setSubscriptionQty(0)
                                    setSubscriptionPlan('')
                                    setLoading(false)

                                    actions.workspaceUpdate({
                                        subscriptionId: null,
                                        subscriptionQty: 0,
                                        subscriptionPlan: null,
                                    })
                                })
                                .catch(() => {
                                    setLoading(false)
                                    setError('Something has gone wrong')
                                })
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleDescriptionSave = async (html, json, editor) => {
        const description = html
        const mentions = getMentionsfromTipTapJSON(json)
        setDescription(description)
    }

    const handlePaymentDetailsUpdate = async () => {
        setError(null)
        try {
            const LemonSqueezy = window['LemonSqueezy']
            const { url } = await workspacesAPI.getLemonSqueezyPaymentUrl(workspaceId, subscriptionId)
            LemonSqueezy.Url.Open(url)
        } catch (e) {
            setError('Something has gone wrong')
        }
    }

    const handlePaddlePayment = (plan) => {
        const Paddle: any = window['Paddle']

        // prod
        if (window.location.href.includes('web.fronteer.app')) {
            Paddle.Initialize({ token: 'live_d2beed0f09a4785cc33eadab587' })

            Paddle.Checkout.open({
                customData: {
                    workspace_id: workspaceId,
                },
                items:
                    plan == 'user'
                        ? [
                              {
                                  priceId: 'pri_01jpj49szc55kc4yk94bhxz03f',
                                  quantity: seats,
                              },
                          ]
                        : [
                              {
                                  priceId: 'pri_01jpj4afy8a27kyss2bvsxgpq6',
                                  quantity: 1,
                              },
                          ],
            })

            // dev
        } else {
            Paddle.Environment.set('sandbox')
            Paddle.Initialize({
                token: 'test_341b6905e3ca3a3b2a7b42ffdcc',
            })
            Paddle.Checkout.open({
                customData: {
                    workspace_id: workspaceId,
                },
                items:
                    plan == 'user'
                        ? [
                              {
                                  priceId: 'pri_01jaey93fgzca0v50rj4s484vr',
                                  quantity: seats,
                              },
                          ]
                        : [
                              {
                                  priceId: 'pri_01jaeyav2rxvzc2f2btapsmgwe',
                                  quantity: 1,
                              },
                          ],
            })
        }
    }

    const handleLemonSqueezyPayment = (plan) => {
        const LemonSqueezy = window['LemonSqueezy']

        if (plan == 'user') {
            LemonSqueezy.Url.Open(
                `https://store.fronteer.app/buy/a0984391-a480-4a1e-aecb-bc0a9502be49?media=0&desc=0&embed=1&quantity=${seats}&enabled=765930&checkout[custom][workspace_id]=${workspaceId}&redirect_url=${window.location.href}`
            )
        } else {
            LemonSqueezy.Url.Open(
                `https://store.fronteer.app/buy/7902e59f-01cc-443d-a0d5-32619027e17e?media=0&desc=0&embed=1&enabled=765917&checkout[custom][workspace_id]=${workspaceId}&redirect_url=${window.location.href}`
            )
        }
    }

    const handlePayment = (plan) => {
        if (subscriptionId) {
            handleSubscriptionChange(plan)
        } else {
            //handleLemonSqueezyPayment(plan)
            handlePaddlePayment(plan)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    return (
        <Modal
            disableBackgroundDismiss
            width={900}
            height={675}
            anchor="middle-center"
            onDismiss={onDismiss}
            isVisible={true}
            style={{ '--f-modal-body-padding': 0 }}
            header={
                <>
                    <Heading as="h4">Workspace Settings</Heading>
                    <Flexer />
                    <Error error={error} />
                    <ModalClose onClick={onDismiss} />
                </>
            }>
            <Loading loading={loading} />
            <Tabs
                selected={selected}
                onSelect={setSelected}
                layout="left">
                <TabList
                    width={200}
                    height="100%">
                    <Tab>General</Tab>
                    <Tab>Calendar</Tab>
                    <Tab disabled={!isAdmin}>Subscription</Tab>
                    <Tab disabled={!isAdmin}>Danger Zone</Tab>
                    <View
                        row
                        position="absolute"
                        style={{ bottom: 0, left: 0 }}
                        p="1rem"
                        width="100%">
                        {!!subscriptionPlan && !loading && (
                            <View
                                column
                                gap={5}
                                radius="var(--f-radius)"
                                p="0.75rem 1rem"
                                alignContent="flex-start"
                                alignItems="flex-start"
                                bg="linear-gradient(165deg, var(--f-color-indigo-600), var(--f-color-purple-500))">
                                <Text
                                    fontWeight="bold"
                                    colorToken="indigo-50">
                                    You're Premium ✨
                                </Text>
                                <Text
                                    size="sm"
                                    colorToken="indigo-50">
                                    Thank you for your support. Please report any bugs or issues to{' '}
                                    <a
                                        href="mailto:support@fronteer.app"
                                        style={{ color: 'inherit' }}>
                                        support@fronteer.app
                                    </a>
                                    .
                                </Text>
                            </View>
                        )}
                    </View>
                </TabList>

                <TabPanels>
                    <TabPanel>
                        <Stack spacing={30}>
                            <View>
                                <input
                                    accept="image/png,image/jpg"
                                    type="file"
                                    className="hide"
                                    ref={fileRef}
                                    onChange={handleFileChange}
                                />

                                <Avatar
                                    key={image ?? 'workspace-modal-avatar'}
                                    src={image}
                                    name={name.value}
                                    size="xl"
                                    width={150}
                                    height={150}
                                    className="f-buttonize"
                                    onClick={handleFileClick}
                                />

                                <Button
                                    m="0.5rem 0 0 0"
                                    disabled={!isAdmin}
                                    prefix={<IconLib icon="paperclip" />}
                                    onClick={handleFileClick}>
                                    Change image
                                </Button>
                            </View>
                            <View flex={1}>
                                <Form onSubmit={handleSubmit}>
                                    <Stack
                                        direction="vertical"
                                        spacing={20}>
                                        <FormControl>
                                            <FormLabel
                                                required
                                                htmlFor="name">
                                                Name
                                            </FormLabel>
                                            <InputControl>
                                                <InputPrefix>
                                                    <ColorButton
                                                        color={color}
                                                        onChange={setColor}
                                                        colors={COLORS}
                                                    />
                                                </InputPrefix>
                                                <Input
                                                    placeholder="Marketing"
                                                    type="text"
                                                    selectOnFocus
                                                    value={name.value}
                                                    onChange={name.onChange}
                                                />
                                            </InputControl>
                                        </FormControl>

                                        <FormControl>
                                            <Label
                                                row
                                                gap={5}
                                                justifyContent="flex-start">
                                                Workspace description
                                            </Label>
                                            <div className="f-input-control modal__description">
                                                <Tiptap
                                                    content={description}
                                                    onUpdate={handleDescriptionSave}
                                                    workspaceId={workspaceId}
                                                    projectId={projectId}
                                                    teamId={null}
                                                    hideToolbar
                                                />
                                            </div>
                                            <FormHelperText kind="info">
                                                This description will show on the workspace join page.
                                            </FormHelperText>
                                        </FormControl>

                                        <Button
                                            type="submit"
                                            variant="accent"
                                            disabled={loading || !name.value || !isAdmin}>
                                            Save
                                        </Button>
                                    </Stack>
                                </Form>
                            </View>
                        </Stack>
                    </TabPanel>
                    <TabPanel>
                        <Form onSubmit={handleSubmit}>
                            <Stack
                                direction="vertical"
                                spacing="1rem">
                                <Heading>Working Hours</Heading>
                                <Text>Working hours reflected on the calendar.</Text>
                                <View
                                    row
                                    gap={5}>
                                    <View flex={1}>
                                        <Select
                                            width="100%"
                                            placeholder="Work hours start"
                                            selected={workHoursStartSelect}
                                            onSelect={(option, dismiss) => {
                                                setWorkHoursStart(option.key)
                                                dismiss()
                                            }}
                                            options={workingHours.map((hour) => ({
                                                key: hour,
                                                label: hour,
                                            }))}
                                        />
                                    </View>
                                    <View flex={1}>
                                        <Select
                                            width="100%"
                                            placeholder="Work hours end"
                                            selected={workHoursEndSelect}
                                            onSelect={(option, dismiss) => {
                                                setWorkHoursEnd(option.key)
                                                dismiss()
                                            }}
                                            options={workingHours.map((hour) => ({
                                                key: hour,
                                                label: hour,
                                            }))}
                                        />
                                    </View>
                                </View>
                                <Heading>Week Length</Heading>
                                <Options
                                    selected={weekLength - 3}
                                    onOptionChange={(index) => setWeekLength(index + 3)}>
                                    <Option>3 days</Option>
                                    <Option>4 days</Option>
                                    <Option>5 days</Option>
                                    <Option>6 days</Option>
                                    <Option>7 days</Option>
                                </Options>
                                <Button
                                    type="submit"
                                    variant="accent"
                                    disabled={loading || !isAdmin}>
                                    Save
                                </Button>
                            </Stack>
                        </Form>
                    </TabPanel>
                    <TabPanel>
                        {/*   {nextBillDate && (
                            <Notification variant="highlight" m="0 0 2rem 0">
                                <NotificationIcon>
                                    <IconLib icon="date" />
                                </NotificationIcon>
                                <NotificationContent>
                                    <Text>Next bill date: {nextBillDate}</Text>
                                </NotificationContent>
                            </Notification>
                        )} */}

                        <View
                            row
                            justifyContent="stretch"
                            alignContent="stretch"
                            alignItems="stretch"
                            width="100%"
                            m="0 auto"
                            gap={20}
                            position="relative"
                            className="pricing__cards">
                            <Card
                                flex={1}
                                column
                                height="auto"
                                p="2rem"
                                gap="1rem"
                                style={{ outline: plan == 'user' ? '4px solid var(--f-color-accent)' : undefined }}
                                alignItems="flex-start"
                                alignContent="flex-start"
                                justifyContent="flex-start"
                                className="f-buttonize-outline"
                                onClick={() => setPlan('user')}>
                                <Badge
                                    bg="linear-gradient(165deg, var(--f-color-indigo-600), var(--f-color-purple-500))"
                                    p="1rem 0.2rem"
                                    colorToken="indigo-50">
                                    Early Access
                                </Badge>
                                <Heading as="h2">User</Heading>
                                <Text colorToken="base-400">
                                    Pay as you scale, perfect for 1 or a handful of people working together.
                                </Text>
                                <View
                                    row
                                    m="1rem 0 0 0"
                                    gap={5}
                                    alignItems="flex-start">
                                    <Heading huge>{3 * seats}</Heading>
                                    <Heading
                                        as="h5"
                                        fontWeight={600}>
                                        USD
                                    </Heading>
                                </View>
                                <Text
                                    size="lg"
                                    colorToken="text-weakest">
                                    {seats} {plural(seats, 'user')}
                                </Text>
                                <Range
                                    min={1}
                                    max={29}
                                    step={1}
                                    value={seats}
                                    onChange={(e: any) => setSeats(+e.target.value)}
                                />
                                <Flexer />
                                <Button
                                    disabled={subscriptionPlan == 'user' && seats == subscriptionQty}
                                    onClick={() => handlePayment('user')}
                                    m="1rem 0 0 0"
                                    size="xl"
                                    width="100%"
                                    variant="accent">
                                    <span
                                        className="f-row"
                                        style={{ gap: 5 }}>
                                        {subscriptionPlan == 'user' && (
                                            <IconLib
                                                icon="check-circle"
                                                size="lg"
                                            />
                                        )}
                                        {subscriptionPlan == 'user'
                                            ? seats == subscriptionQty
                                                ? 'Subscribed'
                                                : 'Update'
                                            : 'Subscribe'}
                                    </span>
                                </Button>
                            </Card>

                            <Card
                                flex={1}
                                column
                                p="2rem"
                                height="auto"
                                gap="1rem"
                                style={{ outline: plan == 'team' ? '4px solid var(--f-color-accent)' : undefined }}
                                alignItems="flex-start"
                                alignContent="flex-start"
                                justifyContent="flex-start"
                                className="f-buttonize-outline"
                                onClick={() => setPlan('team')}>
                                <Badge
                                    bg="linear-gradient(165deg, var(--f-color-indigo-600), var(--f-color-purple-500))"
                                    p="1rem 0.2rem"
                                    colorToken="indigo-50">
                                    Early Access
                                </Badge>
                                <Heading as="h2">Team</Heading>
                                <Text colorToken="base-400">
                                    Scaling up? For workspaces of up to 30 people, with a flat price.
                                </Text>
                                <View
                                    row
                                    m="1rem 0 0 0"
                                    gap={5}
                                    alignItems="flex-start">
                                    <Heading huge>29</Heading>
                                    <Heading
                                        as="h5"
                                        fontWeight={600}>
                                        USD
                                    </Heading>
                                </View>
                                <Text
                                    size="lg"
                                    colorToken="text-weakest"
                                    //textDecoration="line-through"
                                >
                                    Up to 30 people
                                </Text>
                                <Text
                                    size="sm"
                                    m="1rem 0 0 0">
                                    Need more than 30 members?{' '}
                                    <Link
                                        href="mailto:licensing@fronteer.app"
                                        size="sm">
                                        Contact us
                                    </Link>
                                    .
                                </Text>
                                <Flexer />
                                <Button
                                    disabled={subscriptionPlan == 'team'}
                                    onClick={() => handlePayment('team')}
                                    m="1rem 0 0 0"
                                    size="xl"
                                    width="100%"
                                    variant="accent">
                                    <span
                                        className="f-row"
                                        style={{ gap: 5 }}>
                                        {subscriptionPlan == 'team' && (
                                            <IconLib
                                                icon="check-circle"
                                                size="lg"
                                            />
                                        )}
                                        {subscriptionPlan == 'team' ? 'Subscribed' : 'Subscribe'}
                                    </span>
                                </Button>
                            </Card>
                        </View>
                        <br />
                        <Disclaimer />
                        <br />
                        {!!subscriptionId && (
                            <View row>
                                <Link
                                    onClick={handleSubscriptionDelete}
                                    colorToken="danger">
                                    Click here to cancel your subscription immediately.
                                </Link>
                            </View>
                        )}
                        {/*     {!!subscriptionId && (
                            <View
                                row
                                p="0.5rem 0 0 0">
                                <Link
                                    onClick={handlePaymentDetailsUpdate}
                                    colorToken="highlight">
                                    Update payments details.
                                </Link>
                            </View>
                        )} */}
                    </TabPanel>
                    <TabPanel>
                        <Stack
                            direction="vertical"
                            spacing="1rem">
                            <Heading>Hold up!</Heading>
                            <Text>Deleting a workspace is permanent & non recoverable.</Text>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                prefix={<Icon icon={TbTrash} />}>
                                Delete Workspace
                            </Button>
                        </Stack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Modal>
    )
}
